// De vier fasen als hoofdindeling — T2.5.
//
// De fasen wáren al de hoofdindeling, maar alleen impliciet: drie losse routes
// (/acquisitie, /realisatie, /operatie) zonder iets dat zegt dat het één
// indeling is, en een vierde fase (Verloren) zonder scherm — twee Kavels die
// op geen enkele fase-pagina te zien waren. Deze strip maakt de indeling
// zichtbaar, telt elke fase, en geeft Verloren en de restgroep een klikbare
// bestemming (het Archief-scherm, waar ze sinds T2.5 staan).
//
// De stadia zie je één niveau dieper: als kolommen op de fase-pagina zelf, en
// op de Kavel. Deze strip toont ze bewust niet — dat is het inzoomen.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import {
  KAVEL_FASEN_MET_REST,
  telFasen,
  type KavelFaseOfRest,
} from '@/lib/chargingStages';

interface KavelFaseStripProps {
  /** Welke fase het huidige scherm toont. */
  actief: KavelFaseOfRest;
  /** `/crm/charging` of `/charging` — het scherm weet dat, de strip niet. */
  basePath: string;
}

/** Waar een fase te zien is. Verloren en de restgroep wonen op Archief. */
function bestemming(fase: KavelFaseOfRest, basePath: string): string {
  if (fase === 'verloren' || fase === 'onbekend') return `${basePath}/archief`;
  return `${basePath}/${fase}`;
}

export function KavelFaseStrip({ actief, basePath }: KavelFaseStripProps) {
  const navigate = useNavigate();

  const { data: kavels = [] } = useQuery({
    queryKey: ['charging-locations', 'fase-strip'],
    // `select('*')`: dormant_at staat niet in de gegenereerde types en bestaat
    // vóór migratie 084 niet als kolom. Een smalle kolomlijst zou dan met
    // 42703 falen; een `*` levert simpelweg het veld niet, en `kavelZicht`
    // leest dat als 'actief'. 34 rijen, dus de breedte kost niets.
    queryFn: async () => {
      const { data, error } = await supabase
        .from('charging_locations')
        .select('*')
        .is('deleted_at', null);
      if (error) throw error;
      return data ?? [];
    },
  });

  // De telling zelf staat in `lib/chargingStages.ts` en wordt daar getoetst;
  // dit component tekent hem alleen.
  const tellingen = useMemo(() => telFasen(kavels as any[]).perFase, [kavels]);

  return (
    <div className="flex flex-wrap items-stretch gap-2" data-testid="kavel-fase-strip">
      {tellingen.map((telling) => {
        const info = KAVEL_FASEN_MET_REST.find((f) => f.id === telling.fase)!;
        const isActief = telling.fase === actief;
        // De restgroep hoort leeg te zijn. Zolang dat zo is, hoeft hij niet in
        // beeld; zodra er iets in valt, staat hij er — dat is zijn hele reden.
        if (telling.fase === 'onbekend' && telling.actief + telling.slapend === 0) return null;

        return (
          <button
            key={telling.fase}
            type="button"
            onClick={() => navigate(bestemming(telling.fase, basePath))}
            className={cn(
              'flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors',
              isActief ? 'border-primary bg-primary/5' : 'hover:bg-muted/50',
            )}
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: info.color }}
            />
            <span className={cn('text-sm', isActief && 'font-semibold')}>{info.label}</span>
            <Badge variant="secondary" className="h-5 text-xs">
              {telling.actief}
            </Badge>
            {telling.slapend > 0 && (
              <span
                className="text-xs text-muted-foreground"
                title={`${telling.slapend} slapend — standaard verborgen`}
              >
                +{telling.slapend} slapend
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
